import type { ConferenceNewsItem, ConferenceNewsListResp } from './types';

const DEFAULT_NEWS_PAGE_SIZE = 3;
const BEIJING_TIME_ZONE = 'Asia/Shanghai';
const beijingDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BEIJING_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

type ConferenceNewsRawItem = Partial<ConferenceNewsItem> & {
  ID?: string | number | null;
  id?: string | number | null;
  title?: string | number | null;
  titleEn?: string | number | null;
  summary?: string | number | null;
  summaryEn?: string | number | null;
  tags?: string[] | string | null;
  tagsEn?: string[] | string | null;
  coverImage?: string | number | null;
  publishTime?: string | number | null;
  targetURL?: string | number | null;
  targetUrl?: string | number | null;
};

const normalizeString = (value?: string | number | null) =>
  `${value ?? ''}`.trim();

const uniqueNewsTags = (values: string[]) => {
  const seen = new Set<string>();

  return values.filter((value) => {
    if (!value || seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

const splitTagText = (value?: string | null) =>
  normalizeString(value)
    .split('、')
    .map((item) => item.trim())
    .filter(Boolean);

export const splitConferenceNewsTags = (value?: string[] | string | null) => {
  if (Array.isArray(value)) {
    return uniqueNewsTags(value.flatMap((item) => splitTagText(item)));
  }

  return uniqueNewsTags(splitTagText(value));
};

export const normalizeConferenceNewsItem = (
  item: ConferenceNewsRawItem
): ConferenceNewsItem => ({
  id: normalizeString(item.id || item.ID),
  title: normalizeString(item.title),
  titleEn: normalizeString(item.titleEn),
  summary: normalizeString(item.summary),
  summaryEn: normalizeString(item.summaryEn),
  tags: splitConferenceNewsTags(item.tags),
  tagsEn: splitConferenceNewsTags(item.tagsEn),
  coverImage: normalizeString(item.coverImage),
  publishTime: normalizeString(item.publishTime),
  targetURL: normalizeString(item.targetURL || item.targetUrl),
});

export const normalizeConferenceNewsData = (
  data?: ConferenceNewsListResp | ConferenceNewsItem[] | null
): ConferenceNewsListResp => {
  const rawList = Array.isArray(data) ? data : data?.list || [];
  const list = rawList.map((item) => normalizeConferenceNewsItem(item));

  return {
    list,
    total:
      typeof (data as ConferenceNewsListResp | undefined)?.total === 'number'
        ? (data as ConferenceNewsListResp).total || 0
        : list.length,
    page:
      typeof (data as ConferenceNewsListResp | undefined)?.page === 'number'
        ? (data as ConferenceNewsListResp).page || 1
        : 1,
    pageSize:
      typeof (data as ConferenceNewsListResp | undefined)?.pageSize === 'number'
        ? (data as ConferenceNewsListResp).pageSize || DEFAULT_NEWS_PAGE_SIZE
        : DEFAULT_NEWS_PAGE_SIZE,
    conferenceYear: (data as ConferenceNewsListResp | undefined)
      ?.conferenceYear,
  };
};

export const getConferenceNewsText = (
  item: ConferenceNewsItem,
  field: 'title' | 'summary',
  locale: 'zh' | 'en'
) => {
  if (field === 'title') {
    return locale === 'zh'
      ? item.title || item.titleEn
      : item.titleEn || item.title;
  }

  return locale === 'zh'
    ? item.summary || item.summaryEn
    : item.summaryEn || item.summary;
};

export const getConferenceNewsTags = (
  item: ConferenceNewsItem,
  locale: 'zh' | 'en'
) => {
  if (locale === 'zh') {
    return item.tags || [];
  }
  return item.tagsEn || [];
};

export const formatConferenceNewsPublishDate = (value?: string) => {
  const normalizedValue = normalizeString(value);
  if (!normalizedValue) {
    return '-';
  }
  const isTimestamp = /^\d+$/.test(normalizedValue);
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
  const dateTimeValue = normalizedValue.includes('T')
    ? normalizedValue
    : normalizedValue.replace(/-/g, '/');
  const timestamp = isTimestamp
    ? Number(normalizedValue) > 100000000000
      ? Number(normalizedValue)
      : Number(normalizedValue) * 1000
    : Date.parse(
        hasTimeZone
          ? dateTimeValue
          : `${dateTimeValue}${
              dateTimeValue.includes(':') ? '' : ' 00:00:00'
            }+08:00`
      );

  if (Number.isNaN(timestamp)) {
    return normalizedValue;
  }

  const parts = beijingDateFormatter.formatToParts(new Date(timestamp));
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;

  return `${getPart('year')}/${getPart('month')}/${getPart('day')}`;
};
